import { Component } from "react";

const FreshchatInitState = {
	IDLE: "IDLE",
	LOADING: "LOADING",
	ERRORED: "ERRORED",
	INITIALISED: "INITIALISED",
};

var freshchatServiceState = FreshchatInitState.IDLE;

const FallBackErrorMessage = "Something went Wrong!";

const WIDGET_LOAD_TIMEOUT = 10000; // 10 seconds

class FreshchatService extends Component {
	/**
	 * Waits for the Freshchat widget to be ready.
	 * Resolves immediately if already loaded, otherwise listens for the fcWidgetReady event.
	 * Times out after WIDGET_LOAD_TIMEOUT ms.
	 */
	static waitForWidget() {
		return new Promise((resolve, reject) => {
			// If widget is already ready, resolve immediately
			if (window.fcWidgetReady === true) {
				resolve();
				return;
			}

			// Set up timeout
			const timeout = setTimeout(() => {
				window.removeEventListener("fcWidgetReady", onReady);
				reject(new Error("Freshchat widget load timed out"));
			}, WIDGET_LOAD_TIMEOUT);

			// Listen for the ready event
			const onReady = () => {
				clearTimeout(timeout);
				resolve();
			};

			window.addEventListener("fcWidgetReady", onReady, { once: true });
		});
	}

	static async initialize(user_id) {
		if (freshchatServiceState === FreshchatInitState.INITIALISED) {
			console.log("Freshchat is already initialised");
			return { success: true };
		}

		if (freshchatServiceState === FreshchatInitState.LOADING) {
			console.log("Freshchat is already initialising");
			return { error: "Freshchat is already initialising" };
		}

		freshchatServiceState = FreshchatInitState.LOADING;

		try {
			// Wait for the Freshchat widget to be ready
			await this.waitForWidget();

			// Check if fcWidget is available (should be after waitForWidget resolves)
			if (typeof window.fcWidget === "undefined") {
				freshchatServiceState = FreshchatInitState.ERRORED;
				console.log("Freshchat widget is not loaded");
				return { error: "Freshchat widget is not loaded" };
			}

			// Initialize Freshchat widget with user identification
			window.fcWidget.init({
				externalId: user_id,
			});

			console.log("Freshchat Initialised");
		} catch (error) {
			console.log("Could not initialize Freshchat due to - " + error.message);
			freshchatServiceState = FreshchatInitState.ERRORED;
			return { error: error.message || error };
		}

		freshchatServiceState = FreshchatInitState.INITIALISED;
		return { success: true };
	}

	static setUserDetails(email) {
		try {
			if (typeof window.fcWidget === "undefined") {
				return { error: "Freshchat widget is not loaded" };
			}

			window.fcWidget.user.setProperties({
				email: email,
			});

			return { success: true };
		} catch (error) {
			console.log("Could not set user details due to - " + error.message);
			return { error: error?.message || FallBackErrorMessage };
		}
	}

	static async openFreshchatHelpDesk() {
		return new Promise((resolve) => {
			if (freshchatServiceState === FreshchatInitState.LOADING) {
				resolve({ error: "Freshchat is still initialising" });
				return;
			}

			if (freshchatServiceState !== FreshchatInitState.INITIALISED) {
				resolve({ error: "Freshchat has not been initialized" });
				return;
			}

			try {
				if (typeof window.fcWidget === "undefined") {
					resolve({ error: "Freshchat widget is not loaded" });
					return;
				}

				// Listen for widget open event
				window.fcWidget.on("widget:opened", () => {
					resolve({ success: true });
				});

				window.fcWidget.open();

				// Fallback timeout in case event doesn't fire
				setTimeout(() => {
					resolve({ success: true });
				}, 3000);
			} catch (error) {
				resolve({ error: error?.message || FallBackErrorMessage });
			}
		});
	}

	static clearUser() {
		try {
			if (typeof window.fcWidget !== "undefined") {
				window.fcWidget.user.clear();
				freshchatServiceState = FreshchatInitState.IDLE;
			}
		} catch (error) {
			console.log("Could not clear Freshchat user due to - " + error.message);
		}
	}
}

export default FreshchatService;

